import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { LangContext } from '../i18n/LangContext';
import { Rocket, Target, Lightbulb, BarChart2, Mail, User, Lock, Eye, EyeOff, KeyRound, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';

const Register = () => {
  const { register } = useContext(AuthContext);
  const { t } = useContext(LangContext);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const navigate = useNavigate();

  const validatePassword = (pwd) => {
    if (pwd.length < 6) return 'Le mot de passe doit contenir au moins 6 caractères';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pwdError = validatePassword(mot_de_passe);
    if (pwdError) {
      setError(pwdError);
      return;
    }
    if (mot_de_passe !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (!acceptTerms) {
      setError('Vous devez accepter les conditions');
      return;
    }

    setLoading(true);
    try {
      const result = await register(nom, email, mot_de_passe);
      
      // Si vérification email requise
      if (result?.emailVerificationRequired) {
        setRegistrationSuccess(true);
        setRegisteredEmail(result.email);
      } else {
        // Ancien comportement (connexion directe)
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('register_error') || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (!mot_de_passe) return { level: 0, text: '', color: '', bgColor: '' };
    let score = 0;
    if (mot_de_passe.length >= 6) score++;
    if (mot_de_passe.length >= 10) score++;
    if (/[A-Z]/.test(mot_de_passe)) score++;
    if (/[0-9]/.test(mot_de_passe)) score++;
    if (/[^A-Za-z0-9]/.test(mot_de_passe)) score++;
    
    if (score <= 2) return { level: score, text: 'Faible', color: 'text-red-600', bgColor: 'bg-red-500' };
    if (score <= 3) return { level: score, text: 'Moyen', color: 'text-yellow-600', bgColor: 'bg-yellow-500' };
    return { level: score, text: 'Fort', color: 'text-green-600', bgColor: 'bg-green-500' };
  };

  const strength = getPasswordStrength();

  const benefits = [
    { icon: <Rocket className="text-white" size={24} />, title: 'Zéro capital requis', desc: 'Lancez votre business sans investissement initial' },
    { icon: <Target className="text-white" size={24} />, title: 'Trouvez des partenaires', desc: 'Connectez-vous avec des talents complémentaires' },
    { icon: <Lightbulb className="text-white" size={24} />, title: 'Partagez les ressources', desc: 'Accédez au matériel et compétences des autres' },
    { icon: <BarChart2 className="text-white" size={24} />, title: 'Gestion transparente', desc: 'Suivez les finances et répartissez les gains équitablement' },
  ];

  const stats = [
    { value: '', label: '' }
  ];

  // Affichage du message de succès après inscription
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
              Vérifiez votre email ! <Mail size={24} className="text-indigo-600" />
            </h1>
            
            <p className="text-gray-600 mb-4">
              Nous avons envoyé un lien de vérification à :
            </p>
            
            <p className="text-indigo-600 font-semibold text-lg mb-6 bg-indigo-50 py-2 px-4 rounded-lg inline-block">
              {registeredEmail}
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-yellow-800 text-sm flex items-start gap-2">
                <Lightbulb size={16} className="mt-0.5 flex-shrink-0" />
                <span><span className="font-semibold">Conseil :</span> Si vous ne trouvez pas l'email, vérifiez votre dossier <strong>spam</strong> ou <strong>courrier indésirable</strong>.</span>
              </p>
            </div>
            
            <div className="space-y-3">
              <Link 
                to="/login"
                className="block w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-indigo-700 transition-colors"
              >
                Aller à la page de connexion
              </Link>
            </div>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} DJAMANN - Tous droits réservés
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-500 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-16 h-16 rounded-2xl shadow-2xl object-contain bg-white/10 backdrop-blur-sm p-1"
            />
            <div>
              <h1 className="text-3xl font-bold text-white">DJAMANN</h1>
              <p className="text-white/70 text-sm">Lancez votre business, même avec zéro franc</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Votre idée + Nos outils =<br />
              <span className="text-yellow-300">Votre succès</span>
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              DJAMANN connecte entrepreneurs, talents et ressources. Lancez votre activité sans barrière financière ou matérielle.
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-4 mb-10">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300"
              >
                <span className="text-2xl">{benefit.icon}</span>
                <div>
                  <h3 className="text-white font-semibold">{benefit.title}</h3>
                  <p className="text-white/70 text-sm">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-white/60 text-sm">
            © 2025 DJAMANN. Tous droits réservés.
          </p>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-6 py-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-6">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-16 h-16 mx-auto mb-3 rounded-2xl shadow-lg object-contain"
            />
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              DJAMANN
            </h1>
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Créez votre compte ✨</h2>
            <p className="text-gray-500 text-sm">Rejoignez des milliers d'entrepreneurs ambitieux</p>
          </div>

          {/* Register Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2 text-sm">
                <AlertTriangle size={16} />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <User size={14} /> Nom complet
                </label>
                <input
                  type="text"
                  placeholder="Jean Dupont"
                  value={nom}
                  onChange={e => setNom(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <Mail size={14} /> Adresse email
                </label>
                <input
                  type="email"
                  placeholder="vous@exemple.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <Lock size={14} /> Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={mot_de_passe}
                    onChange={e => setMotDePasse(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {/* Password Strength */}
                {mot_de_passe && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div 
                          key={i} 
                          className={`h-1.5 flex-1 rounded-full transition-all ${i <= strength.level ? strength.bgColor : 'bg-gray-200'}`} 
                        />
                      ))}
                    </div>
                    <p className={`text-xs font-medium ${strength.color}`}>
                      Force : {strength.text}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <KeyRound size={14} /> Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white pr-12 ${
                      confirmPassword && confirmPassword !== mot_de_passe 
                        ? 'border-red-300 bg-red-50' 
                        : confirmPassword && confirmPassword === mot_de_passe 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && confirmPassword !== mot_de_passe && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <XCircle size={12} /> Les mots de passe ne correspondent pas
                  </p>
                )}
                {confirmPassword && confirmPassword === mot_de_passe && mot_de_passe && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle size={12} /> Les mots de passe correspondent
                  </p>
                )}
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={e => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                  J'accepte les{' '}
                  <a href="/terms" className="text-purple-600 hover:text-purple-800 font-medium underline">
                    conditions d'utilisation
                  </a>{' '}
                  et la{' '}
                  <a href="/privacy" className="text-purple-600 hover:text-purple-800 font-medium underline">
                    politique de confidentialité
                  </a>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !acceptTerms}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-purple-200 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform hover:-translate-y-0.5 disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Création en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    🚀 Créer mon compte
                  </span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">Déjà membre ?</span>
              </div>
            </div>

            {/* Login Link */}
            <Link
              to="/login"
              className="w-full py-3 border-2 border-purple-200 text-purple-600 font-semibold rounded-xl hover:bg-purple-50 hover:border-purple-300 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>👋</span>
              Se connecter
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-6 flex items-center justify-center gap-4 text-gray-400 text-xs flex-wrap">
            <div className="flex items-center gap-1">
              <span>🔐</span> Données sécurisées
            </div>
            <div className="flex items-center gap-1">
              <span>🚫</span> Sans spam
            </div>
            <div className="flex items-center gap-1">
              <span>💳</span> Inscription gratuite
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
